import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Post } from '../../lib/supabase';
import '../../styles/utilities/spacing.css';
import '../../styles/utilities/layout.css';

interface PostsCardProps {
  posts: Post[];
  loading?: boolean;
  error?: string | null;
  className?: string;
}

const PostsCard: React.FC<PostsCardProps> = ({ posts, loading, error, className = '' }) => {
  const navigate = useNavigate();

  // Get the last 7 posts (most recent)
  const displayPosts = posts.slice(0, 7);

  const handlePostClick = (post: Post) => {
    if (post.route) {
      navigate(post.route);
    }
  };

  const handleSeeAllClick = () => {
    navigate('/posts');
  };

  return (
    <div className={`card no-hover ${className}`}>
      <div className="card-content">
        <h3 className="card-title">Recent Posts</h3>
        <p className="card-description">Our latest news, stories and updates.</p>

        {loading && (
          <div className="posts-list">
            <p style={{ opacity: 0.7 }}>Loading posts...</p>
          </div>
        )}

        {error && !loading && posts.length === 0 && (
          <div className="posts-list">
            <p style={{ opacity: 0.7 }}>Unable to load posts</p>
          </div>
        )}

        {!loading && displayPosts.length > 0 && (
          <div className="posts-list" style={{ marginTop: '1.5rem' }}>
            {displayPosts.map((post, index) => (
              <div
                key={post.id}
                className="post-list-item"
                style={{
                  padding: '0.75rem 1rem',
                  marginBottom: index < displayPosts.length - 1 ? '0.25rem' : 0,
                  borderRadius: '8px',
                  border: 'none',
                  borderLeft: 'none',
                  cursor: post.route ? 'pointer' : 'default',
                  transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onClick={() => handlePostClick(post)}
                onMouseEnter={(e) => {
                  if (post.route) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    const titleSpan = e.currentTarget.querySelector('.post-title') as HTMLElement;
                    if (titleSpan) titleSpan.style.color = '#F9D500';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  const titleSpan = e.currentTarget.querySelector('.post-title') as HTMLElement;
                  if (titleSpan) titleSpan.style.color = 'rgba(255, 255, 255, 0.95)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="post-title" style={{
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.95)',
                    transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}>
                    {post.title}
                  </span>
                  {post.route && (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M6 4L10 8L6 12"
                        stroke="#F9D500"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {post.category && (
                  <span style={{
                    fontSize: '0.85rem',
                    opacity: 0.6,
                    marginTop: '0',
                    display: 'inline-block'
                  }}>
                    {post.category}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && posts.length > 5 && (
          <button
            onClick={handleSeeAllClick}
            className="see-all-button"
            style={{
              marginTop: '1.5rem',
              padding: '0.75rem 1.5rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: 'rgba(255, 255, 255, 0.95)',
              fontSize: '1rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              width: 'fit-content'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            See All Posts
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default PostsCard;